import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import type { ApiError } from '../../api/errors';
import { errorCopy, fieldErrors } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatMoney,
  formatQty,
  type NumberInputValue,
  toMinorUnits,
  typedNumber,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { ITEMS_PATH, LEDGER_PATH, STOCK_PATH, useStockItems } from '../inventory/stock';
import { ORDERS_PATH } from './orders';
import { useVendorBills, type VendorBill } from './RecordCreditForm';

const RECEIPTS_PATH = '/admin/v1/suppliers/receipts';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';
const SUPPLIER_INVOICES_PATH = '/admin/v1/suppliers/supplier-invoices';
const DISCREPANCIES_PATH = '/admin/v1/suppliers/bill-discrepancies';

interface Line {
  key: string;
  /** The line this came from, when it was already on the delivery. Blank on one being added. */
  inventoryItemId: string;
  qty: NumberInputValue;
  /**
   * THE UNIT THIS LINE IS WRITTEN IN, which is the vendor's and not necessarily the shelf's.
   *
   * A delivery line mirrors the factura: "10 cajas" of a beer counted in bottles is stored as ten
   * cases at the price of a case, and the shelf is worked out from it. So the quantity and the price
   * in these boxes are per CASE, and every one of them has to say so. This screen used to carry the
   * figures forward off the line and then label all three — the box, the "Was …" under it, and the
   * price — with the item's stock unit, so an ingested delivery read "10 bottle at L 450.00 a
   * bottle" and submitted `unit: bottle` against a quantity and a price that were per case.
   *
   * A line the operator ADDS is his own, not the vendor's, and it is written in the unit the item is
   * counted in — the same answer `ReceiveDeliveryForm` gives, and the only one he has been offered.
   */
  unit: string;
  unitPrice: NumberInputValue;
  purchaseOrderLineId?: string | null;
  /** What this line said before, so the screen can show what is being changed. */
  wasQty?: number;
  wasUnitPriceMinor?: number;
}

let keySeq = 0;
const blankLine = (): Line => ({
  key: `correction-${++keySeq}`,
  inventoryItemId: '',
  qty: '',
  unit: '',
  unitPrice: '',
});

export interface CorrectableDelivery {
  id: string;
  supplierId: string;
  supplierName: string | null;
  invoiceNumber?: string | null;
  invoiceCurrency: string;
  lines: {
    id: string;
    inventoryItemId: string;
    purchaseOrderLineId?: string | null;
    qty: number;
    unit: string;
    unitPriceMinor: number;
  }[];
}

/**
 * Putting a delivery right, when what went in the box was not what came off the truck.
 *
 * Nine cases typed as ninety at the door is the case this exists for, and until now the only thing
 * anybody could do about it was count the shelf again — which fixes the shelf and leaves the money:
 * the bill, the cost per unit that decides what a drink costs, and the order's own state all keep
 * the wrong number.
 *
 * ## Why this is not an edit screen
 *
 * It looks like one and it is not. What is sent replaces the delivery rather than rewriting it: the
 * original stays on file, exactly as it was typed, with what replaced it and why. That is the
 * record a supplier argument and an auditor both start from, and the screen says so out loud —
 * because a person pressing a button labelled "save" on a form full of their own numbers reasonably
 * assumes the old ones are gone.
 *
 * ## Every line is read back in the unit it was written in
 *
 * A delivery mirrors the vendor's paper: "10 cajas" of a beer counted in bottles is stored as ten
 * CASES at the price of a case, and what goes on the shelf is worked out from that. So the boxes
 * here hold a case count and a case price, and they are labelled in cases. Labelling them with the
 * item's stock unit — which this screen did — turned an ingested delivery into "10 bottle at
 * L 450.00 a bottle" on the way in and submitted `bottle` on the way out, against figures that were
 * never per bottle.
 *
 * ## What is deliberately not asked
 *
 * **Where it was put away.** The goods went where they went, and the server reads that back from
 * the movements the delivery actually wrote rather than from anybody's memory of them.
 *
 * **Who delivered it.** A delivery from the wrong vendor is not a miscount — it is somebody else's
 * goods, on their paper, against their terms — so it is not something a correction can turn into.
 */
export function CorrectDeliveryForm({
  delivery,
  onClose,
  onSaved,
}: {
  delivery: CorrectableDelivery;
  onClose: () => void;
  onSaved: () => void;
}) {
  const bills = useVendorBills(delivery.supplierId, Boolean(delivery.supplierId));
  const bill = (bills.data ?? []).find((b) => b.invoice.receiptId === delivery.id)?.invoice ?? null;

  /*
   * THE FIELDS ARE NOT MOUNTED UNTIL THE BILL IS ON THE TABLE, and this is not a nicety.
   *
   * Four of them below carry the bill forward through `useState(bill?.…)` — an initialiser, which
   * React runs ONCE, on the first render. This modal is opened from the deliveries list, where the
   * vendor's bills have never been fetched, so on that first render `bills.data` is undefined and
   * every one of them initialised to blank and stayed blank. Nothing on the screen said so; the
   * boxes simply looked like a delivery whose paper agreed with itself.
   *
   * What that cost: a bill of $120.00 against $84.00 of goods — ten invoiced, seven delivered, the
   * ordinary morning this whole domain is built around — was restated to $84.00 by a correction
   * that changed a quantity. The $36.00 the vendor over-billed, the row on "Bills that do not
   * agree", and the figure the credit note pre-fills from all went with it, and the reason for it
   * ("short delivery") with them. `dueDate` is sent on every save, so a blank one also wiped the
   * terms off the bill and aged it from the invoice date instead.
   *
   * It was invisible in testing because opening the form a SECOND time works: the query is warm by
   * then and the initialisers see the real values. The first open — the only one that happens in
   * the bar — is the broken one.
   */
  if (!bills.isFetched && delivery.supplierId) {
    return (
      <Modal
        opened
        onClose={onClose}
        title={`Correct the delivery from ${delivery.supplierName ?? 'this supplier'}`}
        size="xl"
        closeOnClickOutside={false}
      >
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Fetching this delivery's bill.
          </Text>
        </Group>
      </Modal>
    );
  }
  return <CorrectDeliveryFields delivery={delivery} bill={bill} onClose={onClose} onSaved={onSaved} />;
}

/** The form itself, mounted only once the bill it carries forward is known. */
function CorrectDeliveryFields({
  delivery,
  bill,
  onClose,
  onSaved,
}: {
  delivery: CorrectableDelivery;
  bill: VendorBill['invoice'] | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const stock = useStockItems();
  const items = useEntityIndex('inventoryItem');
  const queryClient = useQueryClient();

  const currency = bill?.currency ?? delivery.invoiceCurrency;

  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<Line[]>(
    delivery.lines.map((l) => ({
      ...blankLine(),
      inventoryItemId: l.inventoryItemId,
      qty: l.qty,
      // The line's own unit, because the quantity and the price beside it are the line's own.
      unit: l.unit,
      unitPrice: l.unitPriceMinor / 100,
      purchaseOrderLineId: l.purchaseOrderLineId ?? null,
      wasQty: l.qty,
      wasUnitPriceMinor: l.unitPriceMinor,
    })),
  );
  const [invoiceNumber, setInvoiceNumber] = useState(delivery.invoiceNumber ?? '');
  const [invoiceDate, setInvoiceDate] = useState(bill?.invoiceDate ?? '');
  const [dueDate, setDueDate] = useState(bill?.dueDate ?? '');
  /**
   * The total AS PRINTED on their invoice, carried forward only when it already disagreed with the
   * delivery. Blank means the paper and the goods agree, which is what it has always meant.
   */
  const [invoiceTotal, setInvoiceTotal] = useState<NumberInputValue>(
    bill && bill.receivedTotalMinor !== null && bill.receivedTotalMinor !== undefined
      ? bill.totalMinor !== bill.receivedTotalMinor
        ? bill.totalMinor / 100
        : ''
      : '',
  );
  const [discrepancyReason, setDiscrepancyReason] = useState<string | null>(bill?.discrepancyReason ?? null);
  const [error, setError] = useState<string | null>(null);
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const filled = lines.filter((l) => l.inventoryItemId);
  const goodsTotalMinor = filled.reduce(
    (sum, l) => sum + Math.round((toMinorUnits(l.unitPrice) ?? 0) * (typedNumber(l.qty) ?? 0)),
    0,
  );
  const wasTotalMinor = delivery.lines.reduce((sum, l) => sum + Math.round(l.unitPriceMinor * l.qty), 0);
  const statedMinor = toMinorUnits(invoiceTotal);
  const billedMinor = statedMinor ?? goodsTotalMinor;
  const differenceMinor = billedMinor - goodsTotalMinor;
  const disagrees = statedMinor !== undefined && differenceMinor !== 0;
  /*
   * Only the explanations that can be true of a difference with that sign are offered, exactly as
   * the delivery form offers them: a charge only ever makes a bill BIGGER, and goods that never
   * turned up can only leave the paper bigger than the delivery.
   */
  const reasonChoices = disagrees
    ? enumOptions('billDiscrepancyReason').filter(
        (o) =>
          !(differenceMinor > 0 ? ['overDelivery'] : ['shortDelivery', 'unexpectedCharge']).includes(o.value),
      )
    : [];
  const differenceExplained = reasonChoices.some((o) => o.value === discrepancyReason)
    ? discrepancyReason
    : null;

  /** What is actually changing, said in the units the LINE is written in — the sentence he checks. */
  const changed = filled.flatMap((line) => {
    const unit = line.unit;
    const qty = typedNumber(line.qty) ?? 0;
    const price = toMinorUnits(line.unitPrice) ?? 0;
    const said: string[] = [];
    // Thousandths, because these are decimal quantities held as floats: 3 × 0.1 kg is not 0.3.
    if (line.wasQty !== undefined && Math.round(qty * 1000) !== Math.round(line.wasQty * 1000)) {
      said.push(
        `${items.label(line.inventoryItemId)}: ${formatQty(line.wasQty, unit)} → ${formatQty(qty, unit)}`,
      );
    }
    if (line.wasUnitPriceMinor !== undefined && price !== line.wasUnitPriceMinor) {
      said.push(
        `${items.label(line.inventoryItemId)}: ${formatMoney(line.wasUnitPriceMinor, currency)} → ${formatMoney(price, currency)} a ${unit || 'unit'}`,
      );
    }
    if (line.wasQty === undefined) said.push(`${items.label(line.inventoryItemId)} added`);
    return said;
  });
  const dropped = delivery.lines
    .filter((l) => !filled.some((f) => f.inventoryItemId === l.inventoryItemId))
    .map((l) => `${items.label(l.inventoryItemId)} was not on the delivery at all`);

  const setLine = (index: number, patch: Partial<Line>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const save = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) =>
      rawApi<{ settledByACount: string[] }>('POST', `${RECEIPTS_PATH}/${delivery.id}/correct`, {
        reason: reason.trim(),
        invoiceNumber: invoiceNumber.trim() || null,
        ...(invoiceDate ? { invoiceDate } : {}),
        dueDate: dueDate || null,
        // Blank means the paper agrees with what arrived, which is what leaving it out has always
        // meant. Stated, it is what the bill is for.
        ...(statedMinor === undefined
          ? {}
          : { invoiceTotalMinor: statedMinor, discrepancyReason: differenceExplained }),
        lines: filled.map((l) => ({
          inventoryItemId: l.inventoryItemId,
          purchaseOrderLineId: l.purchaseOrderLineId ?? null,
          qty: Number(l.qty),
          // The unit the quantity and the price in the boxes are actually in — see `Line.unit`.
          // Sending the item's stock unit instead said "10 bottle" about ten cases.
          unit: l.unit,
          unitPriceMinor: toMinorUnits(l.unitPrice) ?? 0,
          currency,
        })),
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      }),
    onSuccess: (result) => {
      setFiledMonthWarning(null);
      // A correction moves the same five things receiving moves, and then some: a screen still
      // showing the old figure is how somebody comes to believe it did not save and does it twice.
      for (const key of [
        STOCK_PATH,
        LEDGER_PATH,
        ITEMS_PATH,
        RECEIPTS_PATH,
        PAYABLES_PATH,
        SUPPLIER_INVOICES_PATH,
        DISCREPANCIES_PATH,
        ORDERS_PATH,
      ]) {
        void queryClient.invalidateQueries({ queryKey: listKey(key) });
      }
      onSaved();
      onClose();
      const settled = result?.settledByACount ?? [];
      notifyDone(
        'Delivery corrected',
        settled.length > 0
          ? // The shelf was already put right by somebody who counted it, and saying so is the
            // point: otherwise the reader assumes stock just moved again and goes looking for it.
            `${formatMoney(billedMinor, currency)} is now owed. ${settled
              .map((id) => items.label(id))
              .join(', ')} was left as counted — a stock count had already put that shelf right.`
          : `The shelf and the bill now say ${formatMoney(billedMinor, currency)}. What was written down at the door is kept.`,
      );
    },
    onError: (err: Error) => {
      // A filed month is a question, not a refusal: the correction is real and has to be recorded.
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      /*
       * The refusal stays ON the form, not only in a corner toast. Every one of these names the
       * door that IS open — correct the delivery that replaced this one, ask for a credit note,
       * count that area and write off what is missing — and the person reading it is looking at
       * the form they just pressed the button on, with the toast already fading.
       */
      const first = Object.values(fieldErrors(err))[0];
      setError(first ?? errorCopy(err, 'correct this delivery').message);
      notifyError(err, 'correct this delivery');
    },
  });

  const submit = () => {
    if (reason.trim().length < 3) {
      return setError('Say what was wrong — your accountant and the supplier both read this later');
    }
    if (filled.length === 0) return setError('A delivery has to have at least one thing on it');
    const noQty = filled.find((l) => (typedNumber(l.qty) ?? 0) <= 0);
    if (noQty) {
      return setError(
        `Say how much ${items.label(noQty.inventoryItemId)} really arrived — remove the line if none did`,
      );
    }
    const noPrice = filled.find((l) => typedNumber(l.unitPrice) === undefined);
    if (noPrice) return setError(`Say what ${items.label(noPrice.inventoryItemId)} cost each`);
    // A line added here is written in the unit the item is counted in, so an item with no unit set
    // has nothing to write it in. A line already on the delivery carries the vendor's own.
    const noUnit = filled.find((l) => !l.unit);
    if (noUnit) {
      return setError(
        `${items.label(noUnit.inventoryItemId)} has no unit set — fill in "Counted in" under Stock items first`,
      );
    }
    if (disagrees && !differenceExplained) {
      return setError('Their invoice and what arrived do not agree — say what the difference is');
    }
    const twice = filled.find(
      (l, i) => filled.findIndex((o) => o.inventoryItemId === l.inventoryItemId) !== i,
    );
    if (twice) {
      return setError(
        `${items.label(twice.inventoryItemId)} is on the delivery twice — put the whole amount on one line`,
      );
    }
    setError(null);
    save.mutate({});
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Correct the delivery from ${delivery.supplierName ?? 'this supplier'}`}
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/*
          Said before anything is typed, because a form full of the operator's own numbers with a
          save button on it reads as an edit, and this is not one. What he is about to do is record
          the delivery as it should have been AND keep the one that was typed.
        */}
        <Text size="sm" c="dimmed">
          Put right what was written down. Nothing is erased: the delivery as it was typed stays on file with
          your reason against it, and this replaces it everywhere it counts — the shelf, the bill, what a unit
          really costs, and the order it came off.
        </Text>

        <Textarea
          label="What was wrong"
          description="In your own words. This is what a supplier argument and an audit start from."
          placeholder="Nine cases came off the truck; ninety was typed at the door."
          autosize
          minRows={2}
          maxLength={300}
          required
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          disabled={save.isPending}
        />

        <Divider label="What really arrived" labelPosition="left" styles={{ label: { fontWeight: 600 } }} />

        <Stack gap="xs">
          {lines.map((line, index) => {
            // The line's own unit, not the item's: ten CASES of a beer counted in bottles is what
            // the vendor billed, and the boxes beside this hold a case count and a case price.
            const unit = line.unit;
            const lineTotal = Math.round((toMinorUnits(line.unitPrice) ?? 0) * (typedNumber(line.qty) ?? 0));
            return (
              <Paper key={line.key} withBorder p="xs" radius="sm">
                <Group gap="xs" align="flex-end" wrap="wrap">
                  <div style={{ minWidth: 240, flex: 1 }}>
                    <EntitySelect
                      entity="inventoryItem"
                      label="Item"
                      value={line.inventoryItemId || null}
                      onChange={(v) =>
                        setLine(index, {
                          inventoryItemId: v ?? '',
                          // Whatever unit was on the line belonged to the item that was on it. A
                          // different item is the operator's own line now, in its own counted unit.
                          unit: v ? stock.unitOf(v) : '',
                          purchaseOrderLineId: null,
                        })
                      }
                      disabled={save.isPending}
                    />
                  </div>
                  <NumberInput
                    label="How many"
                    // What the box said before, under the box: the correction is then one number
                    // changed against a figure the operator can see, rather than a fresh entry.
                    description={
                      line.wasQty === undefined
                        ? 'Added by this correction'
                        : `Was ${formatQty(line.wasQty, unit)}`
                    }
                    min={0}
                    style={{ width: 170 }}
                    suffix={unit ? ` ${unit}` : undefined}
                    value={line.qty}
                    onChange={(v) => setLine(index, { qty: v })}
                    disabled={save.isPending}
                  />
                  <NumberInput
                    label={unit ? `Price per ${unit}` : 'Price each'}
                    description={
                      line.wasUnitPriceMinor === undefined
                        ? undefined
                        : `Was ${formatMoney(line.wasUnitPriceMinor, currency)}`
                    }
                    min={0}
                    style={{ width: 170 }}
                    prefix={`${currencySymbol(currency)} `}
                    decimalScale={2}
                    thousandSeparator=","
                    hideControls
                    value={line.unitPrice}
                    onChange={(v) => setLine(index, { unitPrice: v })}
                    disabled={save.isPending}
                  />
                  <Text size="sm" fw={600} pb={6} style={{ width: 110, textAlign: 'right' }}>
                    {lineTotal > 0 ? formatMoney(lineTotal, currency) : ''}
                  </Text>
                  {lines.length > 1 && (
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      color="red"
                      onClick={() => setLines(lines.filter((_, i) => i !== index))}
                      disabled={save.isPending}
                    >
                      Remove
                    </Button>
                  )}
                </Group>
              </Paper>
            );
          })}
          <Group justify="space-between">
            <Button
              size="compact-sm"
              variant="light"
              onClick={() => setLines([...lines, blankLine()])}
              disabled={save.isPending}
            >
              Add something that arrived
            </Button>
            <Text size="sm">
              Was <strong>{formatMoney(wasTotalMinor, currency)}</strong> · now{' '}
              <strong>{formatMoney(goodsTotalMinor, currency)}</strong>
            </Text>
          </Group>
        </Stack>

        <Paper withBorder p="sm" radius="sm">
          <Group grow align="flex-start">
            <TextInput
              label="Invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
              disabled={save.isPending || !bill}
            />
            <DateInput
              label="Dated"
              value={invoiceDate || null}
              onChange={(v) => setInvoiceDate(v ?? '')}
              valueFormat="D MMM YYYY"
              popoverProps={{ withinPortal: true }}
              disabled={save.isPending || !bill}
            />
            <DateInput
              label="To be paid by"
              description="Leave blank if it was due on delivery"
              value={dueDate || null}
              onChange={(v) => setDueDate(v ?? '')}
              valueFormat="D MMM YYYY"
              clearable
              popoverProps={{ withinPortal: true }}
              disabled={save.isPending || !bill}
            />
          </Group>
          <NumberInput
            mt="sm"
            label="Total printed on their invoice"
            description="Leave blank when the paper agrees with what arrived, which is nearly always"
            prefix={`${currencySymbol(currency)} `}
            decimalScale={2}
            thousandSeparator=","
            hideControls
            value={invoiceTotal}
            onChange={setInvoiceTotal}
            disabled={save.isPending || !bill}
          />
          {disagrees && (
            <Select
              mt="sm"
              label="What the difference is"
              description="Only a person can answer this, and the answer decides what happens next"
              required
              data={reasonChoices}
              value={differenceExplained}
              onChange={setDiscrepancyReason}
              comboboxProps={{ withinPortal: true }}
              disabled={save.isPending}
            />
          )}
        </Paper>

        {/* The sentence the operator checks before pressing, in his own units and his own money. */}
        {(changed.length > 0 || dropped.length > 0 || billedMinor !== bill?.totalMinor) && (
          <Alert color="yellow" variant="light" title="What this changes">
            <Stack gap={2}>
              {[...changed, ...dropped].map((said) => (
                <Text key={said} size="sm">
                  {said}
                </Text>
              ))}
              {bill && billedMinor !== bill.totalMinor && (
                <Text size="sm" fw={500}>
                  Their bill: {formatMoney(bill.totalMinor, currency)} → {formatMoney(billedMinor, currency)}
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Record the correction
          </Button>
        </Group>
      </Stack>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ filedMonthReason })}
      />
    </Modal>
  );
}
